import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyAddressUrl, usePropertyField, useCollectionBinding } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Navigation is built from this repo's local src/pages.json, matching the
// approach the base preferred theme's Header uses on its own pages.json. Every
// page AMC wants in the nav is enumerated in this repo's pages.json (with
// name/position/visible overrides), so no cross-package merge is needed at
// import time — a stale relative import into the parent's node_modules
// wouldn't resolve at container serve time anyway.
import pagesData from '../pages.json';
import { isUsableUrl, resolveAddressHref } from '../utils/addressUrl';

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface VirtualTourEntry {
  virtual_tour_url?: string | null;
}

const DAY_ORDER: Record<string, number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

function dayOrderIndex(day: string | null | undefined): number {
  if (!day) return 99;
  const key = day.trim().toUpperCase().slice(0, 3);
  return DAY_ORDER[key] ?? 99;
}

function formatHourTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

function formatHoursValue(openRaw: string | null | undefined, closeRaw: string | null | undefined): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment Only';
  if (!open && !close) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!open || !close) return 'Closed';
  return `${formatHourTime(open)} – ${formatHourTime(close)}`;
}

function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const upper = day.trim().toUpperCase();
  if (upper.length <= 3) return upper;
  return upper.slice(0, 3);
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface NavItem {
  label: string;
  href: string;
}

// Pages reachable through dedicated CTA buttons (utility bar / menu actions)
// rather than the primary navigation list, so they're excluded from the nav
// even when marked visible for routing.
const NAV_EXCLUDE_ALIASES = new Set(['apply-now', 'resident-access']);

interface PageEntry {
  name?: string;
  path?: string;
  position?: number;
  visible?: boolean;
  template?: string;
}

/**
 * Build navigation items dynamically from this repo's pages.json. Respects
 * the `visible` flag, page order (`position`), and excludes the home page
 * (logo handles it), template pages (e.g. hyperlocal), and CTA-only utility
 * pages. Path defaults to `/${alias}` when omitted, matching the runtime's
 * page-resolution convention.
 */
const NAV_ITEMS: NavItem[] = Object.entries(pagesData as Record<string, PageEntry>)
  .filter(([alias, page]) => {
    // Home page is handled by the logo link
    if (alias === 'home' || page.path === '/') return false;
    // Respect visible flag from pages.json (hidden/utility pages, etc.)
    if (page.visible === false) return false;
    // Exclude template pages (e.g. hyperlocal) which are generated dynamically
    if (page.template) return false;
    // Exclude pages surfaced via dedicated CTA buttons
    if (NAV_EXCLUDE_ALIASES.has(alias)) return false;
    return true;
  })
  .sort((a, b) => (a[1].position ?? 0) - (b[1].position ?? 0))
  .map(([alias, page]) => ({
    label: page.name ?? alias,
    href: page.path ?? `/${alias}`,
  }));

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    // No defaultField — mono_white vs mono_black is computed at runtime via luminance
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    // No defaultField — href is resolved at render time from the AnyoneHome
    // scheduler URL (→ /schedule-a-tour) with /contact-us as the fallback.
    // Editors can still override via this field.
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const visitUsLinkDef: SlotGroupDefinition = {
  label: 'Visit Us',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // Validated at render time; unreliable Google URLs fall through to
    // usePropertyAddressUrl() → address search (see resolveAddressHref).
    url: {
      label: 'Google Business URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url', 'property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

// ============================================================================
// Menu Nav Link
// ============================================================================

function MobileNavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) {
  return (
    <a
      href={item.href}
      onClick={onNavigate}
      className="block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_85%,transparent)] hover:text-[var(--vibe-color-text-on-secondary)] transition-colors border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]"
    >
      {item.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  const phoneToken = useContentToken('header.phone');
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;

  // Heading labels for the popped-open menu sections. Plain custom tokens
  // (no defaultField) so editors can rename them without touching property
  // data; defaults match the previously-hardcoded strings.
  const visitUsLabel = useContentToken('header.visit_us_label') || 'Visit Us';
  const callUsLabel = useContentToken('header.call_us_label') || 'Call Us';
  const officeHoursLabel = useContentToken('header.office_hours_label') || 'Office Hours';

  // Visit Us is a slot group (address + directions URL + _target) so the
  // entire block is editable as a unit and the menu's "open in new tab"
  // target stays editor-controlled.
  const visitUs = useSlotGroup('header.visit_us', visitUsLinkDef);
  const fullAddress = visitUs.address || '';
  const propertyAddressUrl = usePropertyAddressUrl();

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);

  // Read both mono variants — no defaultField in logoSlotDef so these are the
  // only source of truth when the editor hasn't set an explicit override.
  const monoWhiteLogoUrl = usePropertyField('property_mono_white_logo') as string | null;
  const monoBlackLogoUrl = usePropertyField('property_mono_black_logo') as string | null;

  // Pick the logo variant that matches the computed text color on the primary
  // background. `--vibe-color-text-on-primary` is set by the tokens generator
  // via getContrastTextColor(property_primary_color) — reading it here keeps
  // the logo flip in lockstep with the text contrast decision (no duplicated
  // luminance math, single source of truth for the threshold).
  // Defaults to mono_white (SSR-safe); updates after mount from the live CSS var.
  const [scrolledLogoVariant, setScrolledLogoVariant] = useState<'mono_white' | 'mono_black'>('mono_white');
  useEffect(() => {
    const textOnPrimary = getComputedStyle(document.documentElement)
      .getPropertyValue('--vibe-color-text-on-primary')
      .trim()
      .toLowerCase();
    // Generator emits '#1a1a1a' for dark text, '#ffffff' for light text.
    // Match dark-text shades to mono_black, otherwise mono_white.
    const isDarkText = textOnPrimary === '#1a1a1a' || textOnPrimary === '#000' || textOnPrimary === '#000000';
    setScrolledLogoVariant(isDarkText ? 'mono_black' : 'mono_white');
  }, []);

  // If the editor has set an explicit logo, use it in both states (no switching).
  // Otherwise: transparent state = always mono_white (hero bg is typically dark),
  //            scrolled state   = luminance-computed variant for primary bg.
  const customLogoUrl: string | null = logoRaw.image || null;
  const transparentLogoUrl = customLogoUrl ?? monoWhiteLogoUrl;
  const scrolledComputedLogoUrl = customLogoUrl ?? (scrolledLogoVariant === 'mono_white' ? monoWhiteLogoUrl : monoBlackLogoUrl);
  // Dual crossfade only needed when the two states would show different images.
  const useDualLogos = !customLogoUrl && !!transparentLogoUrl && !!scrolledComputedLogoUrl && transparentLogoUrl !== scrolledComputedLogoUrl;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);

  // Active tour = property has an AnyoneHome scheduler embed URL (same gate
  // AHScheduler uses). Otherwise send visitors to Contact Us.
  const anyoneHomeSchedulerUrl = usePropertyField('custom_anyonehome_scheduler_url') as string | null;
  const hasActiveTour = isUsableUrl(anyoneHomeSchedulerUrl);
  const scheduleTourHref =
    scheduleTourLink.url || (hasActiveTour ? '/schedule-a-tour' : '/contact-us');

  const officeHoursRaw = useCollectionBinding<OfficeHourEntry>('header.office_hours', {
    collection: 'office_hours',
  });
  const officeHours = [...officeHoursRaw].sort((a, b) => dayOrderIndex(a.day) - dayOrderIndex(b.day));

  // Hide /virtual-tour from nav unless the property has at least one tour
  // with a usable URL (matches VirtualTourEmbed's display rule).
  const virtualTours = useCollectionBinding<VirtualTourEntry>('header.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const navItems = NAV_ITEMS.filter((item) => {
    if (item.href === '/virtual-tour' && !hasVirtualTours) return false;
    return true;
  });

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Validated slot URL → usePropertyAddressUrl → address search → '#'.
  const directionsUrl = resolveAddressHref({
    preferredUrl: visitUs.url,
    propertyAddressUrl,
    fullAddress: String(fullAddress || ''),
    emptyHref: '#',
  });
  const directionsTarget = visitUs._target || '_blank';

  const closeMobileMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full"
        style={{ top: 'var(--offer-banner-height, 0px)', transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {/* ================================================================ */}
        {/* Utility Bar                                                      */}
        {/* ================================================================ */}
        <div className="header-utility-bar">
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-9">
              {/* Left: Address + Phone (desktop) */}
              <div className="hidden md:flex items-center divide-x divide-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_20%,transparent)]">
                <EditableSlotGroup groupPath="header.visit_us" definition={visitUsLinkDef}>
                  <a
                    href={directionsUrl}
                    target={directionsTarget}
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 pr-4 text-xs tracking-wide text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{fullAddress}</span>
                  </a>
                </EditableSlotGroup>

                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref flex items-center gap-1.5 pl-4 text-xs tracking-wide text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken path="header.phone" as="span">
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                )}
              </div>

              {/* Right: Utility CTA links (desktop) */}
              <div className="hidden md:flex items-center divide-x divide-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_20%,transparent)] ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Mobile utility row: phone + directions */}
              <div className="md:hidden flex items-center justify-between w-full text-xs">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref flex items-center gap-1.5 text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-opacity"
                  >
                    <PhoneIcon className="w-3.5 h-3.5" />
                    <span>{formatPhone(phone)}</span>
                  </a>
                )}
                <a
                  href={directionsUrl}
                  target={directionsTarget}
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-opacity"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Main Navigation Bar (transparent)                                */}
        {/* ================================================================ */}
        <div className={`main-nav-bar transition-all duration-500 ease-out ${scrolled ? 'main-nav-bar--scrolled' : ''} ${menuOpen ? 'main-nav-bar--menu-open' : ''}`}>
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {useDualLogos ? (
                    // Two overlapping logos that crossfade on the same 500ms timing
                    // as the header background transition.
                    // "active bg" = scrolled or menu open (header has solid primary color).
                    <div className="relative h-10 lg:h-12 flex items-center">
                      {/* Transparent-state logo (mono_white) — fades out when bg goes solid */}
                      <VibesitesImage
                        url={transparentLogoUrl!}
                        alt={logoRaw.alt || 'Community Logo'}
                        className={`absolute h-10 lg:h-12 w-auto object-contain transition-opacity duration-500 ease-out ${
                          scrolled || menuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                      />
                      {/* Scrolled-state logo (luminance-computed) — fades in when bg goes solid */}
                      <VibesitesImage
                        url={scrolledComputedLogoUrl!}
                        alt={logoRaw.alt || 'Community Logo'}
                        className={`h-10 lg:h-12 w-auto object-contain transition-opacity duration-500 ease-out ${
                          scrolled || menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                      />
                    </div>
                  ) : (transparentLogoUrl || scrolledComputedLogoUrl) ? (
                    <VibesitesImage
                      url={(transparentLogoUrl || scrolledComputedLogoUrl)!}
                      alt={logoRaw.alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className={`font-heading text-xl lg:text-2xl font-bold transition-colors duration-500 ${
                      scrolled || menuOpen ? 'header-logo-text-active' : 'text-white'
                    }`}>
                      The Preferred
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Hamburger — always visible, all navigation lives in the menu */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="relative w-10 h-10 flex items-center justify-center"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Hamburger Menu Overlay — all navigation lives here               */}
        {/* ================================================================ */}
        <div
          className={`fixed inset-x-0 bottom-0 mobile-menu-bg overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))` }}
        >
          <div className="flex flex-col md:flex-row md:items-start">
            {/* Left column: navigation + CTAs */}
            <div className="flex-1 min-w-0 md:border-r md:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]">
              <nav role="navigation" aria-label="Primary navigation">
                {navItems.map((item) => (
                  <MobileNavLink
                    key={item.href}
                    item={item}
                    onNavigate={closeMobileMenu}
                  />
                ))}
              </nav>

              <div className="p-5">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourHref}
                    target={scheduleTourLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className="nav-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
                    <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>
              </div>

              <div className="px-5 pb-5">
                <div className="grid grid-cols-2 gap-2">
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="text-center block py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_30%,transparent)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] hover:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_60%,transparent)] transition-all rounded-sm"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="text-center block py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_30%,transparent)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] hover:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_60%,transparent)] transition-all rounded-sm"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>

            {/* Right column: contact info block, aligned right */}
            <aside
              className="md:w-80 lg:w-96 md:flex-shrink-0 px-5 py-6 border-t md:border-t-0 border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] text-right"
              aria-label="Contact information"
            >
              {fullAddress && (
                <div className="mb-5">
                  <EditableToken path="header.visit_us_label" as="h2" className="menu-contact-label">
                    {visitUsLabel}
                  </EditableToken>
                  <EditableSlotGroup groupPath="header.visit_us" definition={visitUsLinkDef}>
                    <a
                      href={directionsUrl}
                      target={directionsTarget}
                      rel="noopener noreferrer"
                      className="menu-contact-value hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
                    >
                      {fullAddress}
                    </a>
                  </EditableSlotGroup>
                </div>
              )}

              {phone && (
                <div className="mb-5">
                  <EditableToken path="header.call_us_label" as="h2" className="menu-contact-label">
                    {callUsLabel}
                  </EditableToken>
                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref menu-contact-value hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
                  >
                    <EditableToken path="header.phone" as="span">
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>
              )}

              {officeHours.length > 0 && (
                <div>
                  <EditableToken path="header.office_hours_label" as="h2" className="menu-contact-label">
                    {officeHoursLabel}
                  </EditableToken>
                  <dl className="menu-hours-grid">
                    {officeHours.map((h, i) => (
                      <React.Fragment key={i}>
                        <dt className="menu-hours-day">{formatDay(h.day)}</dt>
                        <dd className="menu-hours-time">{formatHoursValue(h.open_time, h.close_time)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </aside>
          </div>
        </div>
      </header>

      <style>{`
        /* ============================================================ */
        /* Utility bar                                                   */
        /* ============================================================ */
        .header-utility-bar {
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
          color: var(--vibe-color-text-on-secondary);
        }

        /* ============================================================ */
        /* Main nav bar — transparent by default, solid on scroll        */
        /* ============================================================ */
        .main-nav-bar {
          background: transparent;
        }
        .main-nav-bar--scrolled {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }

        /* When mobile menu is open, give the nav bar a solid background
           so the hero image doesn't bleed through behind the logo/close row */
        .main-nav-bar--menu-open {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }

        /* ============================================================ */
        /* Fallback text logo when no image is available                 */
        /* ============================================================ */
        .header-logo-text-active {
          color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Hamburger lines — white on transparent, textOnPrimary on solid */
        /* ============================================================ */
        .hamburger-line {
          background-color: white;
          transition: background-color 0.5s ease;
        }
        .main-nav-bar--scrolled .hamburger-line,
        .main-nav-bar--menu-open .hamburger-line {
          background-color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Hamburger menu background                                     */
        /* ============================================================ */
        .mobile-menu-bg {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-secondary) 95%, black) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
        }

        /* ============================================================ */
        /* Hamburger menu contact info block                             */
        /* ============================================================ */
        .menu-contact-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 50%, transparent);
          margin: 0 0 0.4rem;
        }
        .menu-contact-value {
          display: block;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 80%, transparent);
          text-decoration: none;
        }
        .menu-hours-grid {
          display: grid;
          grid-template-columns: auto auto;
          justify-content: end;
          column-gap: 1rem;
          row-gap: 0.2rem;
          margin: 0;
        }
        .menu-hours-day {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          font-weight: 600;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 90%, transparent);
          text-align: left;
        }
        .menu-hours-time {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 60%, transparent);
          text-align: right;
          margin: 0;
        }

        /* ============================================================ */
        /* Schedule tour CTA button                                      */
        /* ============================================================ */
        .nav-cta-btn {
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%
          );
          border-radius: 9999px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.15) 50%,
            transparent 100%
          );
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%,
            var(--vibe-color-primary) 100%
          );
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          left: 100%;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      // Empty so a missing call-tracking number falls through to property_phone
      // in the component (placeholder is applied there as the final fallback).
      defaultValue: '',
    },
    'header.visit_us_label': {
      label: 'Visit Us Heading',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'header.call_us_label': {
      label: 'Call Us Heading',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'header.office_hours_label': {
      label: 'Office Hours Heading',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        // No defaultField — mono_white vs mono_black is computed at runtime via luminance
        image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.visit_us': visitUsLinkDef,
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'property_resident_portal_url', suggestedFields: ['property_resident_portal_url'] },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': scheduleTourLinkDef,
  },
  collectionBindings: {
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
    'header.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
